import React, { useEffect, useRef, useState } from "react";
import { Card, Box, Typography, Rating, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef(null);

  const price = product?.price || 0;
  const mrp = product?.mrp || price;
  const discount =
    mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isHovered) {
      video.currentTime = 0;
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    } else {
      video.pause();
      video.currentTime = 0;
    }
  }, [isHovered]);

  return (
    <Card
      onClick={() => navigate(`/product/${product._id}`)}
      sx={{
        cursor: "pointer",
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: 1,
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: 4,
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* 🔹 IMAGE */}
      <Box
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          position: "relative",
          height: 200,
          backgroundColor: "#f7f7f7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 1,
          overflow: "hidden",
        }}
      >
        {/* 🎬 VIDEO PRODUCTS */}
        {product?.mediaType === "video" || product?.videoUrl || product?.mediaUrl?.includes(".mp4") ? (
          <>
            {/* 📸 THUMBNAIL (Always visible) */}
            <img
              src={product?.thumbnailUrl || "/placeholder-product.png"}
              alt={product?.title}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />

            {/* ▶️ PLAY ICON (Visible when not hovering) */}
            {!isHovered && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(0,0,0,0.18)",
                  pointerEvents: "none",
                }}
              >
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(0,0,0,0.7)",
                    color: "#fff",
                    fontSize: 24,
                    fontWeight: 700,
                  }}
                >
                  ▶
                </Box>
              </Box>
            )}

            {/* 🎥 VIDEO (Pre-rendered always, shown on hover) */}
            <video
              ref={videoRef}
              src={product?.videoUrl}
              muted
              loop
              playsInline
              preload="metadata"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: isHovered ? "block" : "none",
              }}
            />
          </>
        ) : (
          /* 🖼️ IMAGE PRODUCTS */
          <img
            src={product?.thumbnailUrl || "/placeholder-product.png"}
            alt={product?.title}
            style={{
              maxHeight: "100%",
              maxWidth: "100%",
              objectFit: "cover",
            }}
          />
        )}
        {/* 🔥 DISCOUNT BADGE */}
        {discount > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              backgroundColor: "#cc0c39",
              color: "#fff",
              fontSize: 12,
              fontWeight: 600,
              px: 1,
              py: 0.3,
              borderRadius: 1,
            }}
          >
            {discount}% OFF
          </Box>
        )}
      </Box>

      {/* 🔹 CONTENT */}
      <Box sx={{ p: 1.5 }}>
        {/* TITLE */}
        <Tooltip title={product?.title} arrow placement="top">
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              minHeight: 40,
              maxHeight: 40,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              whiteSpace: "normal",
              wordWrap: "break-word",
              overflowWrap: "break-word",
              maxWidth: "min(100%, 40ch)", // Wrap after ~40 characters
            }}
          >
            {product?.title}
          </Typography>
        </Tooltip>

        {/* ⭐ RATING */}
        <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
          <Rating
            value={product?.rating || 0}
            precision={0.5}
            size="small"
            readOnly
          />
          <Typography sx={{ fontSize: 12, ml: 0.5 }}>
            ({product?.ratingCount || 0})
          </Typography>
        </Box>

        {/* 💰 PRICE */}
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: 700, color: "#B12704" }}
          >
            ₹{Number(price || 0).toLocaleString("en-IN")}
          </Typography>

          {mrp > price && (
            <Typography
              variant="body2"
              sx={{
                textDecoration: "line-through",
                color: "#555",
                fontSize: 12,
              }}
            >
              ₹{mrp}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default ProductCard;